import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Feather, ListTodo, Clock, Lightbulb } from "lucide-react";

export const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export const BoxArt = ({ count, open }) => (
  <div className="relative mx-auto w-56 h-36" aria-hidden>
    <div className={`absolute inset-x-0 top-0 h-10 rounded-t-2xl bg-[#B07B4F] border-b-4 border-[#8A5A36] origin-bottom transition-transform duration-700 ${open ? "-rotate-x-[70deg] -translate-y-6 -rotate-6" : ""}`} style={{ transformStyle: "preserve-3d" }} />
    <div className="absolute inset-x-0 top-9 bottom-0 rounded-b-2xl bg-[#C58D5C] shadow-inner flex items-end justify-center gap-1 pb-3 overflow-hidden">
      {Array.from({ length: Math.min(count, 9) }, (_, i) => <span key={i} className="w-5 h-4 bg-[#FFF8E7] rounded-sm shadow" style={{ transform: `rotate(${(i % 3 - 1) * 9}deg) translateY(${(i % 2) * 3}px)` }} />)}
    </div>
    {!open && <div className="absolute left-1/2 top-6 -translate-x-1/2 w-9 h-9 rounded-full bg-[#F6C343] border-2 border-[#8A5A36] flex items-center justify-center text-[#5A3A1E]"><Lock size={16} /></div>}
  </div>
);

export const ReframeWorryButton = ({ w }) => {
  const nav = useNavigate();
  return <button className="mm-btn-ghost !py-2 !px-3 text-sm" onClick={() => nav("/reframe", { state: { situation: w.text } })} data-testid={`worry-reframe-${w.id}`}><Lightbulb size={14} />Reframe this worry</button>;
};

export const SortableWorry = ({ w, onSort }) => {
  const [planning, setPlanning] = useState(false);
  const [step, setStep] = useState("");
  return (
    <div className="mm-card p-4 mm-fade-up" data-testid={`worry-item-${w.id}`}>
      <p className="break-words [overflow-wrap:anywhere]">{w.text}</p>
      {w.know_on && <p className="text-xs text-mm-muted mt-1">You'll know by {new Date(`${w.know_on}T12:00:00`).toLocaleDateString()}</p>}
      {planning ? (
        <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); step.trim() && onSort(w.id, "plan", step.trim()); }}>
          <input autoFocus className="mm-input !py-2 text-sm" maxLength={200} placeholder="One small step I can take…" value={step} onChange={(e) => setStep(e.target.value)} data-testid={`worry-step-input-${w.id}`} />
          <button className="mm-btn !py-2 !px-4 text-sm" disabled={!step.trim()} data-testid={`worry-step-save-${w.id}`}>Save</button>
        </form>
      ) : (
        <div className="flex flex-wrap gap-2 mt-3">
          <button className="mm-btn-ghost !py-2 !px-3 text-sm" onClick={() => onSort(w.id, "let_go")} data-testid={`worry-letgo-${w.id}`}><Feather size={14} />Let it go</button>
          <button className="mm-btn-ghost !py-2 !px-3 text-sm" onClick={() => setPlanning(true)} data-testid={`worry-plan-${w.id}`}><ListTodo size={14} />Plan a small step</button>
          <button className="mm-btn-ghost !py-2 !px-3 text-sm" onClick={() => onSort(w.id, "keep")} data-testid={`worry-keep-${w.id}`}><Clock size={14} />Keep for tomorrow</button>
          <ReframeWorryButton w={w} />
        </div>
      )}
    </div>
  );
};

export const DueCheck = ({ w, onOutcome }) => (
  <div className="mm-card p-4 border-l-4 border-mm-primary" data-testid={`worry-check-${w.id}`}>
    <div className="text-xs font-semibold text-mm-primary uppercase tracking-wide">Did it happen?</div>
    <p className="mt-1 break-words [overflow-wrap:anywhere]">{w.text}</p>
    <div className="flex flex-wrap gap-2 mt-3">
      {[["no", "No"], ["partly", "Partly"], ["coped", "Yes, and I coped"]].map(([k, l]) => (
        <button key={k} className="mm-chip !text-sm" onClick={() => onOutcome(w.id, k)} data-testid={`worry-outcome-${k}-${w.id}`}>{l}</button>
      ))}
    </div>
  </div>
);

export const Evidence = ({ e }) => {
  if (!e.checked) return null;

  return (
    <div className="rounded-3xl bg-mm-primary/10 p-5" data-testid="worry-evidence">
      <div className="text-xs font-semibold text-mm-primary uppercase tracking-wide">Your evidence</div>
      <p className="text-lg font-semibold mt-1">You've checked {e.checked} worr{e.checked === 1 ? "y" : "ies"}. {e.didnt_happen} didn't happen{e.partly ? `, ${e.partly} only partly happened` : ""}{e.coped ? `, and you coped with ${e.coped}` : ""}.</p>
    </div>
  );
};
